// Npm modules
import { useState, useEffect, FC, memo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
// Components
import GradientButton from "./components/GradientButton";
import Tab from "../../shared/tab/Tab";
// Shared components
import GradientText from "../../shared/GradientText/GradientText";
import InputGradientext from "../../shared/GradientTextInput/GradientTextInput";
// Actions
import {
  checkEmail,
  setLoading,
  signIn,
  updatePreAuth,
} from "../../../actions";
// Selectors
import {
  getErrorMessage,
  getPreAuthDetails,
  getLoading,
  getEmailConfirmed,
  getUserClaims,
  getAccessToken,
} from "../../../selectors";
// Utilities
import { deleteAuthState } from "../../../utilities/auth";
import { isEmailValid } from "../../../utilities/customFunctions";
import history from "../../../history";
// Constant
import { Images } from "../../../constants";
// Style component
import {
  StyledPasswordContainer,
  StyledImage,
  StyledForgetPasswordContainer,
  ErrorMessage,
} from "./styled.component";
// Scss
import styles from "./Login.module.scss";
import moment from "moment";
import Loading from "../../shared/Loader/Loading";

const Login: FC = () => {
  // local state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, togglePasswordVisible] = useState(false);
  const forgotPasswordRef = useRef(null);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);

  // action dispatch function
  const dispatch = useDispatch();

  // selectors
  const [
    preAuthDetails,
    isLoading,
    errorMessage,
    emailConfirmed,
    authReducer,
    view,
  ] = useSelector((store: any) => [
    getPreAuthDetails(store),
    getLoading(store),
    getErrorMessage(store),
    getEmailConfirmed(store),
    store.authReducer,
    store.generalReducer.view,
  ]);

  // location
  const location = useLocation();
  const isDisabled = preAuthDetails.status
    ? !isEmailValid(email) || password.length === 0
    : !isEmailValid(email);

  const {
    container,
    container__login,
    text,
    text__subheader,
    container__subheader,
    container__inputs,
    container__actions,
  } = styles;

  useEffect(() => {
    if (view !== undefined) {
      forgotPasswordRef?.current?.click();
    }
  }, [view]);

  useEffect(() => {
    if (emailConfirmed) {
      setIsEmailConfirmed(true);
    }
  }, [emailConfirmed]);

  // run after re render
  useEffect(() => {
    deleteAuthState();
    dispatch(updatePreAuth({}));
  }, []);

  useEffect(() => {
    errorMessage && dispatch(setLoading({ loading: false, errorMessage: "" }));
  }, [email, password]);

  const handleCheckEmail = (email: string) => {
    const authData: any = {
      AuthFlow: "ADMIN_NO_SRP_AUTH",
      AuthParameters: {
        USERNAME: email ? email.toLowerCase().trim() : undefined,
        PASSWORD: password,
      },
      ClientMetadata: {
        secretToBypassPreAuthTrigger: "WxYhd896hK%$j*hG!",
      },
    };

    if (isEmailValid(email) && !isEmailConfirmed) {
      dispatch(checkEmail(authData));
    }

    if (preAuthDetails.status === "CONFIRMED") {
      dispatch(
        signIn({
          ...authData,
          isRedirectToChangeEmailView:
            new URLSearchParams(location.search).get("view") === "change-email",
          queryString: location.search,
        })
      );
    }
  };

  const InvalidEmailError = () => {
    return (
      <ErrorMessage>
        Incorrect email. Please try again or contact{" "}
        <a href="mailto:support@ayacare.com">support@ayacare.com</a>
      </ErrorMessage>
    );
  };

  return (
    <div className={container}>
      <div className={container__login} data-testid="login-container">
        <GradientText string={"Login / Enrol"} />

        <div className={container__subheader}>
          <h2 className={`${text} ${text__subheader}`}>
            Please login / enrol using the same email your employer used when
            the account was created
          </h2>
        </div>

        <div className={container__inputs}>
          <InputGradientext
            name={"email"}
            handleChange={(event: any) => {
              setIsEmailConfirmed(false);
              setEmail(event.target.value);
            }}
            placeholder={"Email"}
            propValues={{
              autoComplete: "off",
            }}
            errorMessage={
              errorMessage === "User does not exist" ? (
                <InvalidEmailError />
              ) : (
                errorMessage
              )
            }
          />

          {preAuthDetails.status === "CONFIRMED" && (
            <>
              <StyledPasswordContainer>
                <InputGradientext
                  name={"password"}
                  handleChange={(event: any) => setPassword(event.target.value)}
                  placeholder={"Password"}
                  propValues={{
                    autoComplete: "off",
                    type: passwordVisible ? "text" : "password",
                  }}
                />
                <StyledImage
                  src={
                    Images[passwordVisible ? "closedEyeIcon" : "openEyeIcon"]
                  }
                  onClick={() => togglePasswordVisible(!passwordVisible)}
                  data-testid="login-icon-input"
                />
              </StyledPasswordContainer>
              <StyledForgetPasswordContainer
                onClick={() => history.push("/reset-password", { modal: true })}
                data-testid="login-forgot-link"
                ref={forgotPasswordRef}
              >
                <GradientText
                  string="Forgot Password"
                  fontSize={"1.25rem"}
                  cursor={"pointer"}
                />
              </StyledForgetPasswordContainer>
            </>
          )}
        </div>

        <div className={container__actions}>
          <GradientButton
            handleClick={() => handleCheckEmail(email)}
            isLoading={isLoading}
            disabled={isDisabled || errorMessage}
            testid={"login-button"}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(Login);
